import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  ActivityIndicator,
} from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { apiRequest } from "../lib/api";
import { formatTime } from "../lib/format";

async function adminFetch<T>(path: string, method?: string): Promise<T> {
  return apiRequest<T>(path, method ? { method } : undefined);
}

export default function AdminUserDetail() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      adminFetch(`/dashadmin/users/${id}`)
        .then(setData)
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [id]);

  function formatDate(d: string) {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color="#1B2A4A" />
      </SafeAreaView>
    );
  }

  if (!data?.user) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <Text style={{ color: "#dc2626" }}>User not found</Text>
      </SafeAreaView>
    );
  }

  const { user, dogs, runs } = data;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Header */}
      <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14, backgroundColor: "#1B2A4A" }}>
        <TouchableOpacity onPress={() => router.back()} style={{ marginRight: 12 }}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontWeight: "800", color: "#fff", flex: 1 }}>User Detail</Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {/* User info card */}
        <View style={{ backgroundColor: "#fff", borderRadius: 14, padding: 20, borderWidth: 1, borderColor: "#e5e7eb", marginBottom: 16 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 14 }}>
            <View style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: "#F0F2F5", alignItems: "center", justifyContent: "center" }}>
              <Ionicons name="person" size={24} color="#1B2A4A" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 20, fontWeight: "800", color: "#1B2A4A" }}>{user.name}</Text>
              <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 2 }}>{user.email}</Text>
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8, marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>
            <View style={{ alignItems: "center", flex: 1 }}>
              <Text style={{ fontSize: 20, fontWeight: "800", color: "#F5A623" }}>{dogs.length}</Text>
              <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Dogs</Text>
            </View>
            <View style={{ alignItems: "center", flex: 1 }}>
              <Text style={{ fontSize: 20, fontWeight: "800", color: "#10B981" }}>{runs.length}</Text>
              <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Runs</Text>
            </View>
            <View style={{ alignItems: "center", flex: 1 }}>
              <Text style={{ fontSize: 14, fontWeight: "700", color: "#1B2A4A" }} numberOfLines={1}>
                {new Date(user.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
              </Text>
              <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Joined</Text>
            </View>
            <View style={{ alignItems: "center", flex: 1 }}>
              <Text style={{ fontSize: 18, fontWeight: "800", color: "#1B2A4A" }} numberOfLines={1}>
                {user.state || "—"}
              </Text>
              <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>State</Text>
            </View>
            <View style={{ alignItems: "center", flex: 1 }}>
              <Text style={{ fontSize: 14, fontWeight: "700", color: "#1B2A4A" }} numberOfLines={1}>
                {user.zip_code || "—"}
              </Text>
              <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Zip</Text>
            </View>
          </View>
        </View>

        {/* Dogs */}
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>
          Dogs ({dogs.length})
        </Text>
        {dogs.length === 0 ? (
          <View style={{ backgroundColor: "#fff", borderRadius: 12, padding: 20, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "center" }}>
            <Text style={{ color: "#9ca3af" }}>No dogs added</Text>
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden", marginBottom: 16 }}>
            {dogs.map((dog: any, i: number) => (
              <View
                key={dog.id}
                style={{
                  padding: 14,
                  borderTopWidth: i > 0 ? 1 : 0,
                  borderTopColor: "#f3f4f6",
                }}
              >
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A" }}>{dog.name}</Text>
                    <Text style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                      {dog.breed}{dog.sex ? ` · ${dog.sex}` : ""}{dog.handicap ? ` · ${parseFloat(dog.handicap).toFixed(1)}×` : ""}
                    </Text>
                    {dog.registeredName && (
                      <Text style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic", marginTop: 2 }} numberOfLines={2}>"{dog.registeredName}"</Text>
                    )}
                    {dog.akcRegistrationNumber && (
                      <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>AKC# {dog.akcRegistrationNumber}</Text>
                    )}
                  </View>
                  <View style={{ alignItems: "flex-end", flexShrink: 0 }}>
                    <Text style={{ fontSize: 14, fontWeight: "700", color: "#10B981" }}>{dog.run_count} runs</Text>
                    {dog.best_speed && (
                      <Text style={{ fontSize: 13, color: "#F5A623", fontWeight: "600", marginTop: 2 }}>
                        {parseFloat(dog.best_speed).toFixed(2)} mph
                      </Text>
                    )}
                    {dog.total_points && (
                      <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        {parseFloat(dog.total_points).toFixed(1)} pts
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Runs */}
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>
          Run History ({runs.length})
        </Text>
        {runs.length === 0 ? (
          <View style={{ backgroundColor: "#fff", borderRadius: 12, padding: 20, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "center" }}>
            <Text style={{ color: "#9ca3af" }}>No runs logged</Text>
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
            {/* Header */}
            <View style={{ flexDirection: "row", backgroundColor: "#f9fafb", paddingVertical: 8, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
              <Text style={{ flex: 1, fontSize: 11, fontWeight: "700", color: "#6b7280" }}>DATE</Text>
              <Text style={{ width: 60, fontSize: 11, fontWeight: "700", color: "#6b7280" }}>DOG</Text>
              <Text style={{ width: 45, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>R#</Text>
              <Text style={{ width: 55, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>TIME</Text>
              <Text style={{ width: 55, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "right" }}>MPH</Text>
              <Text style={{ width: 50, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "right" }}>PTS</Text>
            </View>
            {runs.map((run: any, i: number) => (
              <View
                key={run.id}
                style={{
                  flexDirection: "row",
                  paddingVertical: 10,
                  paddingHorizontal: 14,
                  backgroundColor: i % 2 === 0 ? "#fff" : "#f9fafb",
                  alignItems: "center",
                }}
              >
                <Text style={{ flex: 1, fontSize: 13, color: "#374151" }}>{formatDate(run.date)}</Text>
                <Text style={{ width: 60, fontSize: 13, color: "#6b7280" }} numberOfLines={1}>{run.dog_name}</Text>
                <Text style={{ width: 45, fontSize: 13, color: "#6b7280", textAlign: "center" }}>R{run.runNumber}</Text>
                <Text style={{ width: 55, fontSize: 13, color: "#374151", textAlign: "center" }}>{formatTime(run.timeSeconds)}s</Text>
                <Text style={{ width: 55, fontSize: 13, color: "#F5A623", fontWeight: "600", textAlign: "right" }}>{parseFloat(run.speedMph).toFixed(2)}</Text>
                <Text style={{ width: 50, fontSize: 13, color: "#374151", textAlign: "right" }}>{parseFloat(run.points).toFixed(1)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Delete user */}
        <TouchableOpacity
          onPress={async () => {
            if (Platform.OS === "web") {
              if (!confirm(`Delete ${user.name} (${user.email}) and all their data? This cannot be undone.`)) return;
            }
            try {
              await adminFetch(`/dashadmin/users/${id}`, "DELETE");
              router.replace("/dashadmin" as any);
            } catch {}
          }}
          style={{
            marginTop: 32,
            borderRadius: 12,
            padding: 14,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            borderWidth: 1,
            borderColor: "#fecaca",
            backgroundColor: "#fef2f2",
          }}
        >
          <Ionicons name="trash-outline" size={18} color="#dc2626" />
          <Text style={{ color: "#dc2626", fontSize: 14, fontWeight: "600" }}>Delete User</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}
